import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { McPageHeader, McPageFooter } from "../components/McPageLayout";
import "./mcImportEngine.css";
import "./mcCategories.css";
import MorpheusCountConfig from "./MorpheusCountConfig";
import McTransferRules from "../components/McTransferRules";
import { getMcActiveOrgId } from "../utils/mcCloudSync";
import { buildDefaultState, buildPreparedData, recomputeEngineState, loadEngineState, loadLatestImportEngineState, saveEngineState, saveEngineStateDurable, applyCategorySettingsAcrossScopes, hydrateCategoryConfigurationFromCloud, MC_ENGINE_EVENT } from "../utils/mcEngineData";
import { canAccessAdminTabs, formatStoreLabel, getActiveStoreScope, useAdminStoreView, useCurrentMcProfile } from "../utils/profileAccess";
import { activeSections, MC_SECTION_EVENT, normalizeSectionId } from "../utils/mcSections";
import { loadSites } from "../utils/mcOrgData";
import { loadActiveInventorySnapshot, loadActiveInventorySnapshotsForScopes, MC_ACTIVE_INVENTORY_EVENT } from "../utils/mcActiveInventory";
import { loadSharedOrderingRecord, removeSharedOrderingOverride } from "../utils/mcSharedOrderingService";
import { loadSharedUnitIntelligenceRecord, removeSharedUnitIntelligenceOverride } from "../utils/mcSharedUnitIntelligenceService";

const cleanText = (value) => String(value || "").trim();
const norm = (value) => cleanText(value).toLowerCase().replace(/\s+/g, " ");
const CONFIG_TAB_MEMORY_KEY = "mc-inventory-configuration-last-tab";
const CONFIG_VIEW_MEMORY_KEY = "mc-inventory-configuration-last-view";
let lastInventoryConfigTab = "rules";
let lastInventoryConfigView = "ordering";
const safeSessionGet = (key) => { try { return sessionStorage.getItem(key) || ""; } catch { return ""; } };
const safeSessionSet = (key, value) => { try { sessionStorage.setItem(key, value); } catch {} };
const SECTION_KEYS = ["rules", "outputs", "groups", "ownership", "ordering", "unitIntelligence"];
const SECTION_LABELS = { rules: "Rules", outputs: "Outputs", groups: "Groups", ownership: "Ownership", ordering: "Ordering", unitIntelligence: "Unit Intelligence" };
const sectionLabel = (key) => SECTION_LABELS[key] || String(key || "Configuration");
const withTimeout = (promise, ms = 12000) => Promise.race([
  Promise.resolve(promise),
  new Promise((_, reject) => setTimeout(() => reject(new Error("Timed out")), ms)),
]);
const sectionKeyFor = (tab, view) => tab === "configuration" ? (view === "unit-intelligence" ? "unitIntelligence" : "ordering") : tab;
const sectionPayload = (state, key) => {
  const config = state?.config || {};
  if (key === "rules") return state?.rules || [];
  if (key === "outputs") return { rules: state?.rules || [], categoryOrder: config.categoryOrder || [] };
  if (key === "groups") return config.categoryGroups || [];
  if (key === "ownership") return config.categoryOwnership || {};
  if (key === "ordering") return config.categoryOrder || [];
  if (key === "unitIntelligence") return { unitLibrary: config.unitLibrary || [], defaultUnitRule: config.defaultUnitRule || {}, categoryUnitRules: config.categoryUnitRules || {} };
  return null;
};
const samePayload = (a,b) => JSON.stringify(a) === JSON.stringify(b);

function sortByCategoryOrder(items = [], categoryOrder = []) {
  const rank = new Map((categoryOrder || []).map((item, index) => [norm(item), index]));
  return [...items].sort((a, b) => {
    const ar = rank.has(norm(a)) ? rank.get(norm(a)) : 99999;
    const br = rank.has(norm(b)) ? rank.get(norm(b)) : 99999;
    if (ar !== br) return ar - br;
    return norm(a).localeCompare(norm(b));
  });
}

function normalizeGroups(groups = [], outputs = [], categoryOrder = []) {
  const outputSet = new Set(outputs.map(norm));
  return (Array.isArray(groups) ? groups : [])
    .map((group) => ({
      name: cleanText(group?.name),
      outputs: sortByCategoryOrder(
        Array.from(new Set((group?.outputs || []).map(norm).filter((item) => outputSet.has(item)))),
        categoryOrder
      ),
    }))
    .filter((group) => group.name);
}

export default function MorpheusCountCategories() {
  const location = useLocation();
  const navigate = useNavigate();
  const configTabs = [
    { key: "rules", label: "Rules", description: "Create outputs by teaching Morpheus how to recognise inventory" },
    { key: "outputs", label: "Outputs", description: "Browse every controlled inventory concept and its full configuration" },
    { key: "groups", label: "Groups", description: "Manage the count buckets used by Live Count" },
    { key: "ownership", label: "Ownership", description: "Assign outputs to accountable operational sections" },
    { key: "configuration", label: "Configuration", description: "Ordering, Unit Intelligence and transfer rules" },
  ];
  const hashTab = String(location.hash || "").replace(/^#/, "");
  const rememberedTab = lastInventoryConfigTab;
  const activeConfigTab = configTabs.some((tab) => tab.key === hashTab) ? hashTab : (configTabs.some((tab) => tab.key === rememberedTab) ? rememberedTab : "rules");
  const currentProfile = useCurrentMcProfile();
  const adminStoreView = useAdminStoreView();
  const activeStoreScope = getActiveStoreScope(currentProfile);
  const inventoryScope = String(adminStoreView || "").toUpperCase() !== "ALL" ? adminStoreView : activeStoreScope;
  const [engine, setEngine] = useState(buildDefaultState(activeStoreScope));
  const [applyConfirm, setApplyConfirm] = useState(false);
  const [applyStatus, setApplyStatus] = useState("");
  const [applyInProgress, setApplyInProgress] = useState(false);
  const [publicationFailures, setPublicationFailures] = useState([]);
  const [newRule, setNewRule] = useState({ type: "contains", value: "", output: "" });
  const [newGroupName, setNewGroupName] = useState("");
  const [groupNameDrafts, setGroupNameDrafts] = useState({});
  const [groupOutputDrafts, setGroupOutputDrafts] = useState({});
  const [sections, setSections] = useState([]);
  const [unmappedDrafts, setUnmappedDrafts] = useState({});
  const [selectedOutput, setSelectedOutput] = useState("");
  const [configurationView, setConfigurationView] = useState(() => lastInventoryConfigView || "ordering");
  const [activeInventoryRows, setActiveInventoryRows] = useState([]);
  const [activeInventorySources, setActiveInventorySources] = useState([]);
  const inventorySourceCacheRef = useRef({ signature: "", sources: [] });
  const autoOverrideSaveRef = useRef(null);

  const isGlobalScope = String(activeStoreScope || "ALL").toUpperCase() === "ALL";
  const globalEngineForScope = !isGlobalScope ? loadEngineState("ALL") : engine;
  const explicitSectionScopes = engine.config?.configurationScopeOverrides || {};
  const activeSectionKey = sectionKeyFor(activeConfigTab, configurationView);
  const inferredSectionOverride = !isGlobalScope && !Object.prototype.hasOwnProperty.call(explicitSectionScopes, activeSectionKey)
    ? !samePayload(sectionPayload(engine, activeSectionKey), sectionPayload(globalEngineForScope, activeSectionKey))
    : false;
  const isSectionOverride = !isGlobalScope && (explicitSectionScopes[activeSectionKey] === true || inferredSectionOverride);
  const configurationScopeMode = isGlobalScope ? "GLOBAL" : (isSectionOverride ? "SITE_OVERRIDE" : "INHERITED");
  const configurationScopeLabel = configurationScopeMode === "GLOBAL" ? "Global source of truth" : configurationScopeMode === "SITE_OVERRIDE" ? "Site override" : "Inherited from All Sites";
  const localSectionVersionMeta = engine.config?.configurationSectionVersions?.[activeSectionKey] || null;
  const inheritedGlobalVersionMeta = !isGlobalScope && !isSectionOverride
    ? globalEngineForScope?.config?.configurationSectionVersions?.[activeSectionKey] || null
    : null;
  const sectionVersionMeta = inheritedGlobalVersionMeta || localSectionVersionMeta;
  const sectionVersion = Number(sectionVersionMeta?.version || 0);
  const sectionVersionText = sectionVersion > 0
    ? `Version ${sectionVersion}${sectionVersionMeta?.publishedAt ? ` · Published ${new Date(sectionVersionMeta.publishedAt).toLocaleString()}` : ""}`
    : "Not published yet";


  useEffect(() => {
    lastInventoryConfigTab = activeConfigTab;
  }, [activeConfigTab]);

  useEffect(() => {
    lastInventoryConfigView = configurationView;
  }, [configurationView]);

  useEffect(() => {
    let cancelled = false;
    const sync = () => {
      const stored = loadEngineState(activeStoreScope);
      if (stored) setEngine(recomputeEngineState(stored));
      else setEngine(buildDefaultState(activeStoreScope));
    };

    // MC494 — learned/category configuration is compact and shared. Hydrate it
    // explicitly before resolving the local engine so another browser's Learn
    // Product decision is visible immediately without loading any inventory
    // snapshot or adding a realtime listener.
    const hydrateSharedConfiguration = async () => {
      await hydrateCategoryConfigurationFromCloud(activeStoreScope).catch(() => null);
      if (!cancelled) sync();
    };

    sync();
    hydrateSharedConfiguration();
    window.addEventListener(MC_ENGINE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      cancelled = true;
      window.removeEventListener(MC_ENGINE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, [activeStoreScope]);


  useEffect(() => {
    // Product hydration is only required by the Outputs workspace. Rules,
    // Groups, Ownership, Ordering and Unit Intelligence can render entirely
    // from compact configuration authorities and must not download inventory.
    if (activeConfigTab !== "outputs") {
      setActiveInventorySources([]);
      setActiveInventoryRows([]);
      return undefined;
    }

    let cancelled = false;
    const hydrateInventory = async ({ force = false } = {}) => {
      const scopes = String(inventoryScope || "ALL").toUpperCase() === "ALL"
        ? loadSites().filter((site) => site?.active !== false).map((site) => site.siteId || site.storeKey || site.name).filter(Boolean)
        : [inventoryScope];
      const uniqueScopes = Array.from(new Set(scopes.length ? scopes : [activeStoreScope]));
      const signature = uniqueScopes.map((scope) => String(scope || "").toLowerCase()).sort().join("|");

      if (!force && inventorySourceCacheRef.current.signature === signature && inventorySourceCacheRef.current.sources.length) {
        setActiveInventorySources(inventorySourceCacheRef.current.sources);
        return;
      }

      let snapshotMap = {};
      if (uniqueScopes.length > 1) {
        // Batch loader deduplicates shared/aliased manifests. Ten sites pointing
        // at one All Sites import now download that immutable snapshot once, not
        // once per site.
        snapshotMap = await loadActiveInventorySnapshotsForScopes(uniqueScopes).catch(() => ({}));
      } else {
        const scope = uniqueScopes[0];
        const snapshot = await loadActiveInventorySnapshot(scope, { refreshCloud: true }).catch(() => null);
        if (snapshot) snapshotMap[scope] = snapshot;
      }

      if (cancelled) return;
      const sources = uniqueScopes.map((scope) => ({
        scope,
        snapshot: snapshotMap?.[scope] || null,
        latestEngine: loadLatestImportEngineState(scope),
      }));
      inventorySourceCacheRef.current = { signature, sources };
      setActiveInventorySources(sources);
    };

    hydrateInventory();
    const onActiveInventoryChange = () => hydrateInventory({ force: true });
    window.addEventListener(MC_ACTIVE_INVENTORY_EVENT, onActiveInventoryChange);
    return () => {
      cancelled = true;
      window.removeEventListener(MC_ACTIVE_INVENTORY_EVENT, onActiveInventoryChange);
    };
  }, [activeConfigTab, activeStoreScope, inventoryScope]);

  useEffect(() => {
    // Rebuild the product directory locally whenever configuration changes.
    // This replaces the old MC_ENGINE_EVENT -> Firestore snapshot reload loop.
    if (activeConfigTab !== "outputs") return;
    const combined = [];
    activeInventorySources.forEach(({ snapshot, latestEngine }) => {
      const rawRows = Array.isArray(snapshot?.rawRows) && snapshot.rawRows.length
        ? snapshot.rawRows
        : (Array.isArray(latestEngine?.rawRows) ? latestEngine.rawRows : []);
      const preparedFallback = Array.isArray(latestEngine?.preparedRows) ? latestEngine.preparedRows : [];
      if (!rawRows.length) {
        combined.push(...preparedFallback);
        return;
      }
      const rows = buildPreparedData(
        rawRows,
        snapshot?.mapping || latestEngine?.mapping || engine.mapping,
        engine.config || snapshot?.config || latestEngine?.config,
        engine.rules || snapshot?.rules || latestEngine?.rules
      );
      combined.push(...(Array.isArray(rows) && rows.length ? rows : preparedFallback));
    });
    const seen = new Set();
    setActiveInventoryRows(combined.filter((row) => {
      const key = `${norm(row?.sku || row?.id)}||${norm(row?.product || row?.name)}||${norm(row?.category)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }));
  }, [activeConfigTab, activeInventorySources, engine.mapping, engine.config, engine.rules]);

  useEffect(() => {
    const syncSections = () => setSections(activeSections());
    syncSections();
    window.addEventListener(MC_SECTION_EVENT, syncSections);
    window.addEventListener("storage", syncSections);
    return () => {
      window.removeEventListener(MC_SECTION_EVENT, syncSections);
      window.removeEventListener("storage", syncSections);
    };
  }, []);

  const normalizedOutputs = useMemo(() => {
    const outputSet = new Set();
    (engine.rules || []).forEach((rule) => {
      const clean = norm(rule.output);
      if (clean) outputSet.add(clean);
    });
    (engine.config?.categoryOrder || []).forEach((output) => {
      const clean = norm(output);
      if (clean && outputSet.has(clean)) outputSet.add(clean);
    });
    return sortByCategoryOrder(Array.from(outputSet), engine.config?.categoryOrder || []);
  }, [engine.rules, engine.config?.categoryOrder]);

  const unmappedSourceCategories = useMemo(() => {
    const fromConfig = Array.isArray(engine.config?.unmappedSourceCategories) ? engine.config.unmappedSourceCategories : [];
    const fromRows = (engine.preparedRows || [])
      .filter((row) => row?.isUnmappedCategory)
      .map((row) => norm(row?.sourceCategory));
    return Array.from(new Set([...fromConfig, ...fromRows].map(norm).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [engine.config?.unmappedSourceCategories, engine.preparedRows]);

  const categoryGroups = useMemo(
    () => normalizeGroups(engine.config?.categoryGroups || [], normalizedOutputs, engine.config?.categoryOrder || []),
    [engine.config?.categoryGroups, normalizedOutputs, engine.config?.categoryOrder]
  );

  useEffect(() => {
    setGroupNameDrafts((current) => {
      const next = {};
      categoryGroups.forEach((group) => {
        next[group.name] = current[group.name] ?? group.name;
      });
      return next;
    });
    setGroupOutputDrafts((current) => {
      const next = {};
      categoryGroups.forEach((group) => {
        next[group.name] = current[group.name] || "";
      });
      return next;
    });
  }, [categoryGroups]);

  const groupedOutputSet = useMemo(() => {
    const set = new Set();
    categoryGroups.forEach((group) => (group.outputs || []).forEach((output) => set.add(norm(output))));
    return set;
  }, [categoryGroups]);

  const ungroupedOutputs = normalizedOutputs.filter((output) => !groupedOutputSet.has(norm(output)));

  const sectionById = useMemo(() => {
    const map = new Map();
    sections.forEach((section) => map.set(section.sectionId || section.id, section));
    return map;
  }, [sections]);

  const categoryOwnership = useMemo(() => {
    const raw = engine.config?.categoryOwnership || {};
    const next = {};
    normalizedOutputs.forEach((output) => {
      const cleanOutput = norm(output);
      const sectionId = normalizeSectionId(raw?.[cleanOutput] || raw?.[output] || "");
      if (sectionId && sectionId !== "section" && sectionById.has(sectionId)) next[cleanOutput] = sectionId;
    });
    return next;
  }, [engine.config?.categoryOwnership, normalizedOutputs, sectionById]);

  const ownedOutputCount = useMemo(
    () => normalizedOutputs.filter((output) => Boolean(categoryOwnership[norm(output)])).length,
    [normalizedOutputs, categoryOwnership]
  );

  const outputDirectory = useMemo(() => normalizedOutputs.map((output, index) => {
    const outputKey = norm(output);
    const rules = (engine.rules || []).filter((rule) => norm(rule?.output) === outputKey);
    const products = activeInventoryRows.filter((row) => norm(row?.category || row?.normalizedCategory || row?.output) === outputKey);
    const sectionId = categoryOwnership[outputKey] || "";
    const section = sectionById.get(sectionId);
    const unitRule = engine.config?.categoryUnitRules?.[outputKey] || engine.config?.categoryUnitRules?.[output] || null;
    const assignedGroup = categoryGroups.find((group) => (group.outputs || []).some((item) => norm(item) === outputKey))?.name || "";
    return {
      name: output,
      rules,
      products,
      group: assignedGroup,
      section: section?.name || "Unassigned",
      order: Math.max(1, (engine.config?.categoryOrder || []).findIndex((item) => norm(item) === outputKey) + 1 || index + 1),
      unitRule,
      operational: Boolean(assignedGroup && sectionId),
    };
  }), [normalizedOutputs, engine.rules, activeInventoryRows, engine.config?.categoryUnitRules, engine.config?.categoryOrder, categoryOwnership, sectionById, categoryGroups]);

  const selectedOutputDetails = outputDirectory.find((item) => norm(item.name) === norm(selectedOutput)) || null;

  const persist = async (next) => {
    const scopedNext = {
      ...next,
      config: {
        ...(next.config || {}),
        categoryOverride: !isGlobalScope,
        configurationScopeOverrides: { ...(next.config?.configurationScopeOverrides || engine.config?.configurationScopeOverrides || {}), [activeSectionKey]: !isGlobalScope },
      },
    };
    const recomputed = recomputeEngineState(scopedNext);
    setEngine(recomputed);
    setApplyStatus("Saving Inventory Configuration…");
    const saved = await saveEngineStateDurable(recomputed, activeStoreScope);
    setApplyStatus(
      saved
        ? "Inventory Configuration saved."
        : "Inventory Configuration could not be confirmed. Please retry.",
    );
  };

  const persistGroups = async (groups) => {
    const nextGroups = normalizeGroups(groups, normalizedOutputs, engine.config?.categoryOrder || []);
    const next = recomputeEngineState({
      ...engine,
      config: {
        ...(engine.config || {}),
        categoryGroups: nextGroups,
        categoryOverride: !isGlobalScope,
        configurationScopeOverrides: { ...(engine.config?.configurationScopeOverrides || {}), groups: !isGlobalScope },
      },
    }, { forceInventoryRemap: false });
    setEngine(next);
    setApplyStatus("Saving shared count groups…");
    const saved = await saveEngineStateDurable(next, activeStoreScope);
    setApplyStatus(saved ? "Shared count groups saved." : "Count groups could not be confirmed. Please retry.");
  };

  const addRule = () => {
    if (!newRule.value.trim() || !newRule.output.trim()) return;
    const next = { ...engine, rules: [...engine.rules, { ...newRule, output: norm(newRule.output) }] };
    persist(next);
    setNewRule({ type: "contains", value: "", output: "" });
  };

  const mapUnmappedSource = (sourceCategory, output) => {
    const cleanSource = cleanText(sourceCategory);
    const cleanOutput = norm(output);
    if (!cleanSource || !cleanOutput) return;
    const nextRules = [...(engine.rules || []), { type: "equals", value: cleanSource, output: cleanOutput }];
    persist({ ...engine, rules: nextRules });
    setUnmappedDrafts((current) => {
      const next = { ...current };
      delete next[norm(sourceCategory)];
      return next;
    });
  };

  const updateRule = (index, key, value) => {
    const nextRules = engine.rules.map((rule, i) => (i === index ? { ...rule, [key]: key === "output" ? norm(value) : value } : rule));
    persist({ ...engine, rules: nextRules });
  };

  const removeRule = (index) => {
    const nextRules = engine.rules.filter((_, i) => i !== index);
    persist({ ...engine, rules: nextRules });
  };

  const addGroup = () => {
    const name = cleanText(newGroupName);
    if (!name) return;
    if (categoryGroups.some((group) => norm(group.name) === norm(name))) {
      setNewGroupName("");
      return;
    }
    persistGroups([...categoryGroups, { name, outputs: [] }]);
    setNewGroupName("");
  };

  const removeGroup = (groupName) => {
    persistGroups(categoryGroups.filter((group) => group.name !== groupName));
  };

  const moveGroup = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= categoryGroups.length) return;
    const nextGroups = [...categoryGroups];
    const [group] = nextGroups.splice(index, 1);
    nextGroups.splice(nextIndex, 0, group);
    persistGroups(nextGroups);
  };

  const renameGroup = (oldName) => {
    const nextName = cleanText(groupNameDrafts[oldName]);
    if (!nextName || nextName === oldName) return;
    if (categoryGroups.some((group) => group.name !== oldName && norm(group.name) === norm(nextName))) return;
    persistGroups(categoryGroups.map((group) => (group.name === oldName ? { ...group, name: nextName } : group)));
  };

  const removeOutputFromGroup = (groupName, output) => {
    const cleanOutput = norm(output);
    persistGroups(categoryGroups.map((group) => (
      group.name === groupName
        ? { ...group, outputs: (group.outputs || []).filter((item) => norm(item) !== cleanOutput) }
        : group
    )));
  };

  const addOutputToGroup = (groupName, output) => {
    const cleanOutput = norm(output);
    if (!groupName || !cleanOutput) return;
    const nextGroups = categoryGroups.map((group) => ({
      ...group,
      outputs: (group.outputs || []).filter((item) => norm(item) !== cleanOutput),
    }));
    const targetIndex = nextGroups.findIndex((group) => group.name === groupName);
    if (targetIndex >= 0) {
      nextGroups[targetIndex] = {
        ...nextGroups[targetIndex],
        outputs: Array.from(new Set([...(nextGroups[targetIndex].outputs || []), cleanOutput])),
      };
    }
    persistGroups(nextGroups);
    setGroupOutputDrafts((current) => ({ ...current, [groupName]: "" }));
  };

  const assignOutputToGroup = (output, groupName) => {
    const cleanOutput = norm(output);
    const nextGroups = categoryGroups.map((group) => ({
      ...group,
      outputs: (group.outputs || []).filter((item) => norm(item) !== cleanOutput),
    }));

    if (groupName) {
      const targetIndex = nextGroups.findIndex((group) => group.name === groupName);
      if (targetIndex >= 0) {
        nextGroups[targetIndex] = {
          ...nextGroups[targetIndex],
          outputs: Array.from(new Set([...(nextGroups[targetIndex].outputs || []), cleanOutput])),
        };
      }
    }

    persistGroups(nextGroups);
  };

  const assignOutputToSection = async (output, sectionId) => {
    const cleanOutput = norm(output);
    if (!cleanOutput) return;
    const selectedSection = sections.find((section) => String(section.sectionId || section.id || "") === String(sectionId || ""));
    const cleanSectionId = selectedSection
      ? (selectedSection.sectionId || selectedSection.id)
      : String(sectionId || "").trim();
    const nextOwnership = { ...(engine.config?.categoryOwnership || {}) };
    if (cleanSectionId) nextOwnership[cleanOutput] = cleanSectionId;
    else delete nextOwnership[cleanOutput];

    const next = recomputeEngineState({
      ...engine,
      config: {
        ...(engine.config || {}),
        categoryOwnership: nextOwnership,
        categoryOverride: !isGlobalScope,
        configurationScopeOverrides: { ...(engine.config?.configurationScopeOverrides || {}), ownership: !isGlobalScope },
      },
    }, { forceInventoryRemap: false });
    setEngine(next);
    setApplyStatus("Saving shared ownership…");
    const saved = await saveEngineStateDurable(next, activeStoreScope);
    setApplyStatus(saved ? "Shared ownership saved." : "Ownership could not be confirmed. Please retry.");
  };

  const sectionLabelForOutput = (output) => {
    const sectionId = categoryOwnership[norm(output)];
    return sectionById.get(sectionId)?.name || "Unassigned";
  };

  const ownershipSummary = useMemo(() => {
    return sections.map((section) => ({
      ...section,
      count: normalizedOutputs.filter((output) => categoryOwnership[norm(output)] === (section.sectionId || section.id)).length,
    }));
  }, [sections, normalizedOutputs, categoryOwnership]);

  const markSiteOverrideFromEdit = async (source = "configuration") => {
    if (isGlobalScope || isSectionOverride) return true;
    if (autoOverrideSaveRef.current) return autoOverrideSaveRef.current;

    const task = (async () => {
      const current = loadEngineState(activeStoreScope) || engine;
      const next = recomputeEngineState({
        ...current,
        config: {
          ...(current.config || {}),
          categoryOverride: true,
          categoryOverrideCreatedAt: current.config?.categoryOverrideCreatedAt || new Date().toISOString(),
          categoryOverrideSource: "ALL",
          categoryOverrideReason: source,
          configurationScopeOverrides: { ...(current.config?.configurationScopeOverrides || {}), [source === "unit-intelligence" ? "unitIntelligence" : source]: true },
        },
      }, { forceInventoryRemap: false });
      setEngine(next);
      setApplyStatus("Site override created automatically from this change.");
      const saved = await saveEngineStateDurable(next, activeStoreScope);
      if (!saved) setApplyStatus("The site change was made, but override status could not be confirmed. Please retry.");
      return saved;
    })().finally(() => {
      autoOverrideSaveRef.current = null;
    });

    autoOverrideSaveRef.current = task;
    return task;
  };

  const createSiteOverride = async () => {
    if (isGlobalScope || isSectionOverride) return;
    const next = recomputeEngineState({
      ...engine,
      config: {
        ...(engine.config || {}),
        categoryOverride: true,
        categoryOverrideCreatedAt: new Date().toISOString(),
        categoryOverrideSource: "ALL",
        configurationScopeOverrides: { ...(engine.config?.configurationScopeOverrides || {}), [activeSectionKey]: true },
      },
    }, { forceInventoryRemap: false });
    setEngine(next);
    setApplyStatus("Creating site override…");
    const saved = await saveEngineStateDurable(next, activeStoreScope);
    setApplyStatus(saved ? "Site override created. Changes now apply only to this site." : "Site override could not be confirmed. Please retry.");
  };

  const copySectionFromGlobal = (target, globalState, key) => {
    const next = { ...target, config: { ...(target.config || {}) } };
    const gc = globalState?.config || {};
    if (key === "rules") next.rules = [...(globalState?.rules || [])];
    if (key === "outputs") { next.rules = [...(globalState?.rules || [])]; next.config.categoryOrder = [...(gc.categoryOrder || [])]; }
    if (key === "groups") next.config.categoryGroups = JSON.parse(JSON.stringify(gc.categoryGroups || []));
    if (key === "ownership") next.config.categoryOwnership = { ...(gc.categoryOwnership || {}) };
    if (key === "ordering") next.config.categoryOrder = [...(gc.categoryOrder || [])];
    if (key === "unitIntelligence") { next.config.unitLibrary = [...(gc.unitLibrary || [])]; next.config.defaultUnitRule = { ...(gc.defaultUnitRule || {}) }; next.config.categoryUnitRules = JSON.parse(JSON.stringify(gc.categoryUnitRules || {})); }
    next.config.configurationScopeOverrides = { ...(next.config.configurationScopeOverrides || {}), [key]: false };
    const globalVersion = gc.configurationSectionVersions?.[key];
    next.config.configurationSectionVersions = {
      ...(next.config.configurationSectionVersions || {}),
      ...(globalVersion ? { [key]: { ...globalVersion, inherited: true } } : {}),
    };
    next.config.categoryOverride = Object.values(next.config.configurationScopeOverrides).some(Boolean);
    return recomputeEngineState(next, { forceInventoryRemap: key === "rules" || key === "unitIntelligence" });
  };

  const applyGlobalSectionToAllSites = async () => {
    if (!isGlobalScope || applyInProgress) return;

    const publishSectionKey = activeSectionKey;
    const label = sectionLabel(publishSectionKey);
    const sites = loadSites().filter((site) => site?.active !== false);
    const currentPayloadSignature = JSON.stringify(sectionPayload(engine, publishSectionKey));
    const previousPublication = engine.config?.configurationSectionVersions?.[publishSectionKey] || null;
    const previousPayloadSignature = cleanText(previousPublication?.payloadSignature);
    const globalChanged = !previousPublication ? true : (previousPayloadSignature ? previousPayloadSignature !== currentPayloadSignature : false);
    const nextVersion = Math.max(1, Number(previousPublication?.version || 0) + (globalChanged ? 1 : 0));
    const publishedAt = globalChanged ? new Date().toISOString() : previousPublication?.publishedAt;
    const publication = {
      ...(previousPublication || {}),
      version: nextVersion,
      publishedAt,
      publishedBy: cleanText(currentProfile?.name || currentProfile?.email || ""),
      section: publishSectionKey,
      payloadSignature: currentPayloadSignature,
    };

    // Publishing is authoritative for the selected section. Every active site is
    // rewritten with the global section and marked as inherited. This removes
    // legacy/stale local copies even when an older build failed to persist the
    // configurationScopeOverrides flag correctly. Without this, the UI could say
    // “all sites inherit” while a site still loaded an old local Rules payload.
    const targets = sites.map((site) => {
      const scope = site.siteId || site.storeKey || site.name;
      if (!scope) return null;
      const current = loadEngineState(scope) || buildDefaultState(scope);
      return { scope, current };
    }).filter(Boolean);
    const inherited = 0;

    if (!globalChanged && targets.length === 0) {
      setApplyStatus(`${label} already matches the published global version. No new version was created and no sites required updating.`);
      return;
    }

    setApplyInProgress(true);
    setPublicationFailures([]);
    setApplyStatus(globalChanged
      ? `Publishing ${label} version ${nextVersion}… You may switch tabs while this continues in the background.`
      : `Reapplying ${label} version ${nextVersion} to ${targets.length} site override${targets.length === 1 ? "" : "s"}… You may switch tabs.`);
    safeSessionSet("mc-configuration-background-publication", JSON.stringify({
      section: publishSectionKey,
      label,
      version: nextVersion,
      status: "running",
      startedAt: new Date().toISOString(),
    }));

    let versionedGlobal = engine;
    if (globalChanged) {
      versionedGlobal = {
        ...engine,
        config: {
          ...(engine.config || {}),
          configurationSectionVersions: {
            ...(engine.config?.configurationSectionVersions || {}),
            [publishSectionKey]: publication,
          },
        },
      };
      setEngine(versionedGlobal);
      const globalSaved = await saveEngineStateDurable(versionedGlobal, "ALL", { persistGroups: false, persistOwnership: false });
      if (!globalSaved) {
        setApplyStatus(`${label} version ${nextVersion} could not be published. Please retry.`);
        setApplyInProgress(false);
        safeSessionSet("mc-configuration-background-publication", JSON.stringify({ section: publishSectionKey, label, version: nextVersion, status: "failed", reason: "Global source write was not acknowledged." }));
        return;
      }
    }

    if (targets.length === 0) {
      setApplyStatus(`${label} version ${nextVersion} published and confirmed in the global source.`);
      setApplyInProgress(false);
      safeSessionSet("mc-configuration-background-publication", JSON.stringify({ section: publishSectionKey, label, version: nextVersion, status: "complete", updated: 0, inherited }));
      return;
    }

    setApplyStatus(`${globalChanged ? `Published ${label} version ${nextVersion}. ` : ""}Applying the global section to ${targets.length} active site${targets.length === 1 ? "" : "s"} in the background.`);

    // Detach the site work from the current tab. Navigating elsewhere does not
    // cancel these writes; status is also mirrored into sessionStorage so a
    // remounted Inventory Configuration screen can report the final outcome.
    Promise.all(targets.map(async ({ scope, current }) => {
      const siteName = formatStoreLabel(scope);
      let stage = "Removing local section override";
      const copied = copySectionFromGlobal(current, versionedGlobal, publishSectionKey);
      const next = {
        ...copied,
        config: {
          ...(copied.config || {}),
          configurationSectionVersions: {
            ...(copied.config?.configurationSectionVersions || {}),
            [publishSectionKey]: { ...publication, inherited: true },
          },
        },
      };

      try {
        if (publishSectionKey === "ordering") {
          stage = "Removing local Ordering override";
          removeSharedOrderingOverride(scope);
        }
        if (publishSectionKey === "unitIntelligence") {
          stage = "Removing local Unit Intelligence override";
          removeSharedUnitIntelligenceOverride(scope);
        }

        // Update the immediate browser state first. The cloud commit may take
        // longer, but the site is already semantically inherited once its local
        // source has been removed. Avoid classifying a slow acknowledgement as
        // a failed publication.
        saveEngineState(next, scope);
        stage = "Persisting inherited section metadata";
        const engineSaved = await saveEngineStateDurable(next, scope, { persistGroups: false, persistOwnership: false });
        if (!engineSaved) {
          return { scope, siteName, ok: false, stage, reason: "Firestore did not confirm the inherited site payload." };
        }
        return { scope, siteName, ok: true, syncPending: false };
      } catch (error) {
        return {
          scope,
          siteName,
          ok: false,
          stage,
          reason: cleanText(error?.message || error || "Unknown publication error"),
        };
      }
    })).then((results) => {
      const updated = results.filter((result) => result.ok && !result.syncPending).length;
      const failures = results.filter((result) => !result.ok);
      const syncPending = results.filter((result) => result.ok && result.syncPending).length;
      setPublicationFailures(failures);
      setApplyStatus(failures.length || syncPending
        ? `${label} version ${nextVersion} is globally published. ${updated} site${updated === 1 ? "" : "s"} confirmed; ${failures.length + syncPending} site${failures.length + syncPending === 1 ? "" : "s"} could not be confirmed and can be published again.`
        : `${label} version ${nextVersion} published successfully and applied to all ${updated} active site${updated === 1 ? "" : "s"}.`);
      safeSessionSet("mc-configuration-background-publication", JSON.stringify({
        section: publishSectionKey,
        label,
        version: nextVersion,
        status: failures.length ? "attention" : "complete",
        updated,
        inherited,
        failures,
      }));
    }).finally(() => {
      setApplyInProgress(false);
    });
  };

  const revertSiteToGlobal = async () => {
    if (isGlobalScope || !isSectionOverride) return;
    const globalEngine = loadEngineState("ALL");
    if (!globalEngine) { setApplyStatus("Global configuration could not be loaded. Please retry after hydration completes."); return; }
    const nextSectionState = copySectionFromGlobal(engine, globalEngine, activeSectionKey);
    if (activeSectionKey === "ordering") removeSharedOrderingOverride(activeStoreScope);
    if (activeSectionKey === "unitIntelligence") removeSharedUnitIntelligenceOverride(activeStoreScope);
    setEngine(nextSectionState);
    const ok = await saveEngineStateDurable(nextSectionState, activeStoreScope);
    setApplyStatus(ok ? `${activeSectionKey} now inherits All Sites.` : "Revert could not be confirmed. Please retry.");
    return;
  };

  const applyCategoryToAllSites = () => {
    const applied = applyCategorySettingsAcrossScopes(engine);
    setEngine(applied);
    setApplyConfirm(false);
    setApplyStatus("Category settings applied to all sites.");
  };

  const groupForOutput = (output) => {
    const cleanOutput = norm(output);
    return categoryGroups.find((group) => (group.outputs || []).some((item) => norm(item) === cleanOutput))?.name || "";
  };

  if (!canAccessAdminTabs(currentProfile)) {
    return (
      <div className={`mc-enginePage mc-tabPage mc-categoriesPage mc-inventoryConfigTab--${activeConfigTab}`}>
        <div className="mc-enginePage__grid" />
        <div className="mc-enginePage__ambient mc-enginePage__ambient--one" />
        <div className="mc-enginePage__ambient mc-enginePage__ambient--two" />

        <McPageHeader activeKey="categories" subtitle="This area is available to admin profiles only." />

      <nav className="mc-inventoryConfigTabs" aria-label="Inventory Configuration sections">
        {configTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={activeConfigTab === tab.key ? "is-active" : ""}
            onClick={() => { lastInventoryConfigTab = tab.key; navigate(`/morpheus-count/categories#${tab.key}`); }}
          >
            <strong>{tab.label}</strong>
            <span>{tab.description}</span>
          </button>
        ))}
      </nav>
        <McPageFooter />
      </div>
    );
  }

  return (
    <div className={`mc-enginePage mc-tabPage mc-categoriesPage mc-inventoryConfigTab--${activeConfigTab}`}>
      <div className="mc-enginePage__grid" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--one" />
      <div className="mc-enginePage__ambient mc-enginePage__ambient--two" />

      <McPageHeader activeKey="categories" />

      <nav className="mc-inventoryConfigTabs" aria-label="Inventory Configuration sections">
        {configTabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={activeConfigTab === tab.key ? "is-active" : ""}
            onClick={() => { lastInventoryConfigTab = tab.key; navigate(`/morpheus-count/categories#${tab.key}`); }}
          >
            <strong>{tab.label}</strong>
            <span>{tab.description}</span>
          </button>
        ))}
      </nav>

      {!(activeConfigTab === "configuration" && configurationView === "transfers") ? <section className={`mc-configurationScopeBanner mc-configurationScopeBanner--${configurationScopeMode.toLowerCase()}`}>
        <div className="mc-configurationScopeBanner__identity">
          <span>Configuration scope</span>
          <strong>{configurationScopeLabel}</strong>
          <p>
            {configurationScopeMode === "GLOBAL"
              ? "Changes made here become the default for every site. New sites inherit them automatically."
              : configurationScopeMode === "INHERITED"
                ? `${formatStoreLabel(activeStoreScope)} currently inherits the All Sites ${activeSectionKey} configuration.`
                : `${formatStoreLabel(activeStoreScope)} has a local ${activeSectionKey} override.`}
          </p>
        </div>
        <div className="mc-configurationScopeBanner__actions">
          <span className={`mc-scopePill mc-scopePill--${configurationScopeMode.toLowerCase()}`}>{configurationScopeLabel}</span>
          {configurationScopeMode === "GLOBAL" ? (<button className="mc-engineBtn mc-engineBtn--primary" type="button" onClick={applyGlobalSectionToAllSites} disabled={applyInProgress}>{applyInProgress ? "Publishing…" : "Publish Global Changes"}</button>) : null}
          {configurationScopeMode === "INHERITED" ? (
            <button className="mc-engineBtn mc-engineBtn--primary" type="button" onClick={createSiteOverride}>Override this section</button>
          ) : null}
          {configurationScopeMode === "SITE_OVERRIDE" ? (
            <button className="mc-engineBtn mc-engineBtn--ghost" type="button" onClick={revertSiteToGlobal}>Revert this section</button>
          ) : null}
        </div>
        <div className="mc-configurationScopeBanner__status">
          <strong>{sectionLabel(activeSectionKey)} · {sectionVersionText}</strong>
          {applyStatus ? <span>{applyStatus}</span> : null}
        </div>
      </section> : null}

      {activeConfigTab === "configuration" ? (
        <section className="mc-inventoryConfigurationWorkspace">
          <div className="mc-inventoryConfigurationSwitcher" role="tablist" aria-label="Output configuration views">
            <button type="button" className={configurationView === "ordering" ? "is-active" : ""} onClick={() => { setConfigurationView("ordering"); lastInventoryConfigView = "ordering"; }}>
              <strong>Ordering</strong><span>Control display order across Morpheus</span>
            </button>
            <button type="button" className={configurationView === "unit-intelligence" ? "is-active" : ""} onClick={() => { setConfigurationView("unit-intelligence"); lastInventoryConfigView = "unit-intelligence"; }}>
              <strong>Unit Intelligence</strong><span>Conversions, quantity logic and price basis</span>
            </button>
            <button type="button" className={configurationView === "transfers" ? "is-active" : ""} onClick={() => { setConfigurationView("transfers"); lastInventoryConfigView = "transfers"; }}>
              <strong>Transfer rules</strong><span>Stock to keep and amounts to suggest</span>
            </button>
          </div>
          <section className={`mc-inventoryConfigEmbed mc-inventoryConfigEmbed--${configurationView}`}>
            {configurationView === "transfers" ? <McTransferRules key={[getMcActiveOrgId(), activeStoreScope].join(':')} orgId={getMcActiveOrgId()} scope={activeStoreScope} siteLabel={activeStoreScope === "ALL" ? "All sites" : formatStoreLabel(activeStoreScope)} /> : <MorpheusCountConfig embeddedSection={configurationView} onSiteConfigurationEdited={markSiteOverrideFromEdit} />}
          </section>
        </section>
      ) : null}

      <section className="mc-enginePage__heroCards mc-inventoryConfigKpis">
        <article className="mc-engineCard"><span>Rules</span><strong>{engine.rules.length}</strong></article>
        <article className="mc-engineCard"><span>Category Outputs</span><strong>{normalizedOutputs.length}</strong></article>
        <article className="mc-engineCard"><span>Owned Outputs</span><strong>{ownedOutputCount}/{normalizedOutputs.length}</strong></article>
        <article className="mc-engineCard"><span>Scope</span><strong>{activeStoreScope === "ALL" ? "All Sites" : formatStoreLabel(activeStoreScope)}</strong></article>
      </section>


      <section className="mc-enginePage__content">
        {activeConfigTab === "outputs" ? <article className="mc-enginePanel mc-outputDirectoryPanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Outputs workspace</h3>
              <p>Rules create outputs. This directory shows what each output controls across groups, ownership, ordering and Unit Intelligence.</p>
            </div>
            <div className="mc-engineBadge">{outputDirectory.length} Outputs</div>
          </div>
          <div className="mc-outputDirectoryGrid">
            {outputDirectory.map((output) => (
              <button type="button" className="mc-outputDirectoryCard" key={`output-card-${output.name}`} onClick={() => setSelectedOutput(output.name)}>
                <div className="mc-outputDirectoryCard__top">
                  <div><span>Normalized output</span><strong>{output.name}</strong></div>
                  <div className="mc-outputDirectoryCard__badges">
                    <em className={output.operational ? "is-operational" : "is-incomplete"}>{output.operational ? "Operational" : "Needs setup"}</em>
                    <span className={`mc-scopeMiniBadge mc-scopeMiniBadge--${configurationScopeMode.toLowerCase()}`}>{configurationScopeLabel}</span>
                  </div>
                </div>
                <div className="mc-outputDirectoryCard__stats">
                  <div><span>Rules</span><strong>{output.rules.length}</strong></div>
                  <div><span>Products</span><strong>{output.products.length}</strong></div>
                  <div><span>Group</span><strong>{output.group || "Ungrouped"}</strong></div>
                  <div><span>Owner</span><strong>{output.section}</strong></div>
                </div>
                <div className="mc-outputDirectoryCard__footer"><span>Order {output.order}</span><b>View output →</b></div>
              </button>
            ))}
          </div>
        </article> : null}

        {activeConfigTab === "ownership" ? <article className="mc-enginePanel mc-categoryOwnershipPanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Category ownership</h3>
              <p>Assign each normalized category output to the section that owns it. This prepares Morpheus for section-based counts and accountability.</p>
            </div>
            <div className="mc-engineBadge">{sections.length} Sections</div>
          </div>

          {sections.length ? (
            <>
              <div className="mc-categoryOwnershipSummary">
                {ownershipSummary.map((section) => (
                  <div className="mc-categoryOwnershipSummary__item" key={section.sectionId || section.id}>
                    <strong>{section.name}</strong>
                    <span>{section.count} outputs owned</span>
                  </div>
                ))}
              </div>

              <div className="mc-categoryOwnershipTable">
                <div className="mc-categoryOwnershipTable__head">
                  <div>Normalized Output</div>
                  <div>Owner Section</div>
                  <div>Current Owner</div>
                </div>
                {normalizedOutputs.map((output) => (
                  <div className="mc-categoryOwnershipTable__row" key={`ownership-${output}`}>
                    <div>
                      <strong>{output}</strong>
                      <span>{groupForOutput(output) ? `Count group: ${groupForOutput(output)}` : "No count group yet"}</span>
                    </div>
                    <select value={categoryOwnership[norm(output)] || ""} onChange={(e) => assignOutputToSection(output, e.target.value)}>
                      <option value="">Unassigned</option>
                      {sections.map((section) => (
                        <option key={`${output}-${section.sectionId || section.id}`} value={section.sectionId || section.id}>
                          {section.name}
                        </option>
                      ))}
                    </select>
                    <div className={categoryOwnership[norm(output)] ? "mc-categoryOwnerPill" : "mc-categoryOwnerPill mc-categoryOwnerPill--empty"}>
                      {sectionLabelForOutput(output)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="mc-emptyState">Create sections first, then return here to assign category ownership.</div>
          )}
        </article> : null}

        {activeConfigTab === "groups" ? <>
        <article className="mc-enginePanel mc-configGroupsPanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Count groups</h3>
              <p>Create main count buckets, then assign normalized categories into them. Live Count will use these groups.</p>
            </div>
            <div className="mc-engineBadge">Live Count Sections</div>
          </div>

          <div className="mc-categoryGroupCreator">
            <div className="mc-fieldControl">
              <label>New group name</label>
              <input
                placeholder="Buds, Pre Rolls, Edibles..."
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addGroup();
                  }
                }}
              />
            </div>
            <button className="mc-engineBtn mc-engineBtn--primary" type="button" onClick={addGroup}>Create Group</button>
          </div>

          <div className="mc-categoryGroupGrid">
            {categoryGroups.map((group, groupIndex) => {
              const selectableOutputs = normalizedOutputs.filter((output) => !(group.outputs || []).some((item) => norm(item) === norm(output)));
              return (
                <div className="mc-categoryGroupCard" key={group.name}>
                  <div className="mc-categoryGroupCard__top mc-categoryGroupCard__top--edit">
                    <div className="mc-categoryGroupNameEdit">
                      <label>Group name</label>
                      <input
                        value={groupNameDrafts[group.name] ?? group.name}
                        onChange={(e) => setGroupNameDrafts((current) => ({ ...current, [group.name]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            renameGroup(group.name);
                          }
                        }}
                        onBlur={() => renameGroup(group.name)}
                      />
                    </div>
                    <div className="mc-categoryGroupActions mc-categoryGroupActions--order">
                      <button
                        className="mc-engineBtn mc-engineBtn--ghost mc-engineBtn--tiny"
                        type="button"
                        onClick={() => moveGroup(groupIndex, -1)}
                        disabled={groupIndex === 0}
                        title="Move group left"
                      >
                        ←
                      </button>
                      <button
                        className="mc-engineBtn mc-engineBtn--ghost mc-engineBtn--tiny"
                        type="button"
                        onClick={() => moveGroup(groupIndex, 1)}
                        disabled={groupIndex === categoryGroups.length - 1}
                        title="Move group right"
                      >
                        →
                      </button>
                      <button className="mc-engineBtn mc-engineBtn--ghost mc-engineBtn--tiny" type="button" onClick={() => removeGroup(group.name)}>Remove</button>
                    </div>
                  </div>
                  <p>{group.outputs.length} category outputs assigned</p>
                  <div className="mc-categoryGroupAddOutput">
                    <select
                      value={groupOutputDrafts[group.name] || ""}
                      onChange={(e) => setGroupOutputDrafts((current) => ({ ...current, [group.name]: e.target.value }))}
                    >
                      <option value="">Add output to this group...</option>
                      {selectableOutputs.map((output) => (
                        <option key={`${group.name}-option-${output}`} value={output}>{output}</option>
                      ))}
                    </select>
                    <button className="mc-engineBtn mc-engineBtn--ghost mc-engineBtn--tiny" type="button" onClick={() => addOutputToGroup(group.name, groupOutputDrafts[group.name])}>Add</button>
                  </div>
                  <div className="mc-categoryGroupPills mc-categoryGroupPills--editable">
                    {group.outputs.map((output) => (
                      <button type="button" key={`${group.name}-${output}`} onClick={() => removeOutputFromGroup(group.name, output)} title="Remove output from group">
                        <span>{output}</span>
                        <b>×</b>
                      </button>
                    ))}
                    {!group.outputs.length ? <em>No outputs assigned yet</em> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </article>

        <article className="mc-enginePanel mc-configGroupAssignmentsPanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Assign outputs to groups</h3>
              <p>These are your normalized category outputs. Assign each one to a higher-level count group.</p>
            </div>
            <div className="mc-engineBadge">{ungroupedOutputs.length} Ungrouped</div>
          </div>

          <div className="mc-categoryAssignTable">
            <div className="mc-categoryAssignTable__head">
              <div>Normalized Output</div>
              <div>Count Group</div>
            </div>
            {normalizedOutputs.map((output) => (
              <div className="mc-categoryAssignTable__row" key={output}>
                <div>
                  <strong>{output}</strong>
                  {!groupForOutput(output) ? <span>Not grouped yet</span> : null}
                </div>
                <select value={groupForOutput(output)} onChange={(e) => assignOutputToGroup(output, e.target.value)}>
                  <option value="">Ungrouped / Other</option>
                  {categoryGroups.map((group) => <option key={`${output}-${group.name}`} value={group.name}>{group.name}</option>)}
                </select>
              </div>
            ))}
          </div>
        </article>
        </> : null}

        {activeConfigTab === "groups" ? <article className="mc-enginePanel mc-configUnmappedPanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Unmapped source categories</h3>
              <p>Raw POS categories live here until you intentionally map them. They no longer become normalized outputs automatically.</p>
            </div>
            <div className="mc-engineBadge">{unmappedSourceCategories.length} Unmapped</div>
          </div>
          <div className="mc-ruleList mc-unmappedList">
            {unmappedSourceCategories.length ? unmappedSourceCategories.map((source) => {
              const sourceKey = norm(source);
              const selectedOutput = unmappedDrafts[sourceKey] || "";
              return (
                <div className="mc-ruleRow mc-unmappedRow" key={`unmapped-${source}`}>
                  <div className="mc-fieldControl mc-unmappedSource">
                    <label>Source category</label>
                    <input value={source} readOnly />
                  </div>
                  <div className="mc-fieldControl mc-unmappedTarget">
                    <label>Normalize to existing output</label>
                    <select
                      value={selectedOutput}
                      onChange={(e) => setUnmappedDrafts((current) => ({ ...current, [sourceKey]: e.target.value }))}
                      disabled={!normalizedOutputs.length}
                    >
                      <option value="">Choose normalized output...</option>
                      {normalizedOutputs.map((output) => (
                        <option key={`map-${source}-${output}`} value={output}>{output}</option>
                      ))}
                    </select>
                    {!normalizedOutputs.length ? (
                      <small>Create a normalization rule below first, then return here to map source categories.</small>
                    ) : (
                      <small>Need a new output? Create it in Add Rule below first.</small>
                    )}
                  </div>
                  <button
                    className="mc-engineBtn mc-engineBtn--primary mc-unmappedMapBtn"
                    type="button"
                    disabled={!selectedOutput}
                    onClick={() => mapUnmappedSource(source, selectedOutput)}
                  >Map</button>
                </div>
              );
            }) : <p className="mc-emptyCopy">No unmapped source categories. New LS categories will appear here instead of polluting outputs.</p>}
          </div>
        </article> : null}

        <article className="mc-enginePanel mc-configAddRulePanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Add rule</h3>
              <p>Quickly add a new normalization rule.</p>
            </div>
            <div className="mc-engineBadge">Category Map</div>
          </div>

          <div className="mc-fieldGrid">
            <div className="mc-fieldControl">
              <label>Match type</label>
              <select value={newRule.type} onChange={(e) => setNewRule({ ...newRule, type: e.target.value })}>
                <option value="contains">contains</option>
                <option value="equals">equals</option>
                <option value="starts_with">starts_with</option>
              </select>
            </div>
            <div className="mc-fieldControl">
              <label>Match value</label>
              <input value={newRule.value} onChange={(e) => setNewRule({ ...newRule, value: e.target.value })} />
            </div>
            <div className="mc-fieldControl">
              <label>Normalized output</label>
              <input value={newRule.output} onChange={(e) => setNewRule({ ...newRule, output: e.target.value })} />
            </div>
          </div>

          <div className="mc-inlineActions" style={{ marginTop: "14px" }}>
            <button className="mc-engineBtn mc-engineBtn--primary" onClick={addRule}>Add Rule</button>
          </div>
        </article>

        <article className="mc-enginePanel mc-configRulesPanel">
          <div className="mc-enginePanel__header">
            <div>
              <h3>Live Rule Table</h3>
              <p>Edit the active rules directly below.</p>
            </div>
            <div className="mc-engineBadge">Live Rules</div>
          </div>

          <div className="mc-liveRuleTable">
            <div className="mc-liveRuleTable__head">
              <div>Match Type</div>
              <div>Match Value</div>
              <div>Normalized Category</div>
              <div>Scope</div>
              <div className="mc-liveRuleTable__headAction">Actions</div>
            </div>

            {engine.rules.map((rule, i) => (
              <div className="mc-liveRuleTable__row" key={`rule-${i}`}>
                <div>
                  <label className="mc-liveRuleLabel">Match Type</label>
                  <select value={rule.type} onChange={(e) => updateRule(i, "type", e.target.value)}>
                    <option value="contains">contains</option>
                    <option value="equals">equals</option>
                    <option value="starts_with">starts_with</option>
                  </select>
                </div>
                <div>
                  <label className="mc-liveRuleLabel">Match Value</label>
                  <input value={rule.value} onChange={(e) => updateRule(i, "value", e.target.value)} />
                </div>
                <div>
                  <label className="mc-liveRuleLabel">Normalized Category</label>
                  <input value={rule.output} onChange={(e) => updateRule(i, "output", e.target.value)} />
                </div>
                <div><span className={`mc-scopeMiniBadge mc-scopeMiniBadge--${configurationScopeMode.toLowerCase()}`}>{configurationScopeLabel}</span></div>
                <div className="mc-liveRuleAction">
                  <button className="mc-engineBtn mc-engineBtn--ghost" type="button" onClick={() => removeRule(i)}>
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        </article>
      </section>

      {selectedOutputDetails ? (
        <div className="mc-outputWorkspaceModal" role="dialog" aria-modal="true" aria-label={`${selectedOutputDetails.name} output workspace`}>
          <button className="mc-outputWorkspaceModal__backdrop" type="button" onClick={() => setSelectedOutput("")} aria-label="Close output workspace" />
          <section className="mc-outputWorkspaceModal__panel">
            <header>
              <div><span>Output workspace</span><h2>{selectedOutputDetails.name}</h2><p>One view of every permanent configuration attached to this normalized output.</p></div>
              <button type="button" onClick={() => setSelectedOutput("")}>×</button>
            </header>
            <div className="mc-outputWorkspaceSummary">
              <article><span>Rules</span><strong>{selectedOutputDetails.rules.length}</strong></article>
              <article><span>Products</span><strong>{selectedOutputDetails.products.length}</strong></article>
              <article><span>Count Group</span><strong>{selectedOutputDetails.group || "Ungrouped"}</strong></article>
              <article><span>Owner</span><strong>{selectedOutputDetails.section}</strong></article>
              <article><span>Order</span><strong>{selectedOutputDetails.order}</strong></article>
              <article><span>Unit</span><strong>{selectedOutputDetails.unitRule?.countUnit || "Standard"}</strong></article>
            </div>
            <div className="mc-outputWorkspaceColumns">
              <article>
                <h3>Rules feeding this output</h3>
                {selectedOutputDetails.rules.length ? selectedOutputDetails.rules.map((rule, index) => (
                  <div className="mc-outputWorkspaceRule" key={`${selectedOutputDetails.name}-rule-${index}`}><span>{rule.type}</span><strong>{rule.value}</strong></div>
                )) : <p>No active rules feed this output.</p>}
              </article>
              <article>
                <h3>Configuration assignment</h3>
                <dl>
                  <div><dt>Group</dt><dd>{selectedOutputDetails.group || "Ungrouped"}</dd></div>
                  <div><dt>Section owner</dt><dd>{selectedOutputDetails.section}</dd></div>
                  <div><dt>Display order</dt><dd>{selectedOutputDetails.order}</dd></div>
                  <div><dt>Unit Intelligence</dt><dd>{selectedOutputDetails.unitRule ? `${selectedOutputDetails.unitRule.countUnit || "Unit"} → ${selectedOutputDetails.unitRule.baseUnit || "Unit"}` : "Standard unit"}</dd></div>
                </dl>
              </article>
            </div>
            <article className="mc-outputWorkspaceProducts">
              <div className="mc-outputWorkspaceProducts__header">
                <div><h3>Products using this output</h3><p>Products from the active inventory snapshot currently normalized to {selectedOutputDetails.name}.</p></div>
                <span>{selectedOutputDetails.products.length} products</span>
              </div>
              {selectedOutputDetails.products.length ? (
                <div className="mc-outputWorkspaceProducts__list">
                  {selectedOutputDetails.products.map((product, index) => (
                    <div key={`${selectedOutputDetails.name}-product-${product?.sku || product?.id || index}`}>
                      <strong>{product?.product || product?.productName || product?.name || product?.description || `Product ${index + 1}`}</strong>
                      <span>{product?.sku || product?.variantSku || product?.code || "No SKU"}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="mc-outputWorkspaceProducts__empty">No products from the active inventory snapshot currently use this output.</p>}
            </article>
            <footer><span>Rules remain the creator. This workspace is the identity and inspection layer.</span><button type="button" onClick={() => setSelectedOutput("")}>Done</button></footer>
          </section>
        </div>
      ) : null}

      {publicationFailures.length > 0 ? (
        <div className="mc-publishFailureBackdrop" role="presentation" onMouseDown={() => setPublicationFailures([])}>
          <section className="mc-publishFailureModal" role="dialog" aria-modal="true" aria-labelledby="mc-publish-failure-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="mc-publishFailureModal__header">
              <div><span>Publication diagnostic</span><h2 id="mc-publish-failure-title">{publicationFailures.length} site{publicationFailures.length === 1 ? "" : "s"} could not be updated</h2></div>
              <button type="button" onClick={() => setPublicationFailures([])} aria-label="Close">×</button>
            </div>
            <p>The global version was published successfully. These site overrides were not replaced:</p>
            <div className="mc-publishFailureModal__list">
              {publicationFailures.map((failure) => (
                <article key={failure.scope}>
                  <strong>{failure.siteName || formatStoreLabel(failure.scope)}</strong>
                  <span>{failure.stage}</span>
                  <p>{failure.reason}</p>
                </article>
              ))}
            </div>
            <div className="mc-publishFailureModal__actions">
              <button className="mc-engineBtn mc-engineBtn--primary" type="button" onClick={() => setPublicationFailures([])}>OK</button>
            </div>
          </section>
        </div>
      ) : null}
      <McPageFooter />
    </div>
  );
}
